import { useState, useEffect, useRef } from 'react';

// convert PCM Float32 to Int16
function convertFloat32ToInt16(input) {
  const output = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return output;
}

export function useAudioRecorderStreamable({ wsRef, isAuthenticated, recording, audioStream }) {
  const [status, setStatus] = useState('inactive');
  const [secondsRecorded, setSecondsRecorded] = useState(0);
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const processorRef = useRef(null);
  const storedChunks = useRef([]);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const statusRef = useRef(status);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  async function init() {
    if (!audioStream) {
      console.warn('No audio stream provided to useRecordAudioStreamable');
      return;
    }

    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(audioStream);
      const processor = audioCtx.createScriptProcessor(4096, 1, 1);
      
      processor.onaudioprocess = e => {
        if (statusRef.current === 'recording') {
          const data = e.inputBuffer.getChannelData(0);
          const pcm16 = convertFloat32ToInt16(data);
          const buffer = pcm16.buffer;

          if (isAuthenticatedRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
            storedChunks.current.push(buffer);
            storedChunks.current.forEach(storedChunk => {
              wsRef.current.send(storedChunk);
            });
            storedChunks.current = [];
          } else {
            console.log('WebSocket not ready or not authenticated, storing chunks locally');
            storedChunks.current.push(buffer);
          }
        }
      };
      
      source.connect(processor);
      processor.connect(audioCtx.destination);
      sourceRef.current = source;
      processorRef.current = processor;
      
      console.log('Audio recorder initialized successfully');
    } catch (error) {
      console.error('Error initializing audio recorder:', error);
    }
  }

  useEffect(() => {
    if (recording && audioStream) {      
      init();
      return () => {
        processorRef.current?.disconnect();
        sourceRef.current?.disconnect();
        audioCtxRef.current?.close();
      };
    }
    return null;
  }, [isAuthenticated, wsRef, status, recording, audioStream]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let interval;
    if (status === 'recording') {
      interval = setInterval(() => {
        setSecondsRecorded(sec => sec + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [status]);

  const record = () => {
    setStatus('recording');
    storedChunks.current = [];
    setSecondsRecorded(0);
  };
  const pause = () => setStatus('paused');
  const resume = () => setStatus('recording');
  const stop = () => setStatus('inactive');

  return { record, pause, resume, stop, status, secondsRecorded };
}